// Scenarios.jsx — compare current vs 2030 vs 2050 across portfolio
import React, { useEffect, useState } from "react";
import {
  Box, Paper, CircularProgress, Button, Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from "@mui/material";
import { RefreshCw, TrendingUp } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import { riskApi } from "../api/api";
import { fmtINR, fmtScore, fmtBps, fmtPct, HAZARD_LABELS, riskColor } from "../utils/format";

const SCENS = ["current", "2030", "2050"];

export default function Scenarios() {
  const [data, setData] = useState({});  // {scenario -> portfolioResponse}
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const out = {};
      for (const sc of SCENS) {
        const { data } = await riskApi.cachedPortfolio(sc);
        out[sc] = data;
      }
      setData(out);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const rescoreAll = async () => {
    setRescoring(true);
    try {
      for (const sc of SCENS) {
        await riskApi.rescorePortfolio(sc);
      }
      toast.success("Portfolio re-scored across all scenarios");
      load();
    } catch (e) { toast.error("Re-score failed"); }
    finally { setRescoring(false); }
  };

  return (
    <DashboardLayout
      title="Scenario Analysis"
      subtitle="CURRENT · 2030 · 2050"
      headerExtra={
        <Button size="small" variant="contained" onClick={rescoreAll} disabled={rescoring}
          startIcon={rescoring ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <RefreshCw size={13} />}
          data-testid="rescore-all-scenarios-btn"
          sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}>
          {rescoring ? "Computing…" : "Re-score 3 scenarios"}
        </Button>
      }
    >
      {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box> : (
        <>
          {/* Top KPI strip */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.6, mb: 2 }}>
            {SCENS.map((sc) => {
              const s = data[sc]?.summary || {};
              return (
                <Paper key={sc} sx={{ p: 2.4 }} data-testid={`scen-card-${sc}`}>
                  <Box className="cb-kpi-label">{sc.toUpperCase()}</Box>
                  <Box className="cb-display" sx={{ fontSize: 36, fontWeight: 600, color: "#111827", mt: 0.6 }}>
                    {fmtScore(s.avg_score)}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <RiskPill level={s.risk_level} />
                    <Chip size="small" label={`${s.collateral_count || 0} collaterals`} sx={{ fontSize: 9.5 }} />
                  </Box>
                  <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8 }}>
                    <Box>
                      <Box className="cb-kpi-label">Exposure</Box>
                      <Box className="cb-mono" sx={{ fontSize: 14, fontWeight: 600, mt: 0.3 }}>{fmtINR(s.total_financial_exposure_inr)}</Box>
                    </Box>
                    <Box>
                      <Box className="cb-kpi-label">Avg PD↑</Box>
                      <Box className="cb-mono" sx={{ fontSize: 14, fontWeight: 600, mt: 0.3, color: "#8F5E14" }}>{fmtBps(s.avg_pd_uplift_bps)}</Box>
                    </Box>
                    <Box>
                      <Box className="cb-kpi-label">High-risk</Box>
                      <Box className="cb-mono" sx={{ fontSize: 14, fontWeight: 600, mt: 0.3, color: "#A93226" }}>{fmtPct(s.high_risk_pct)}</Box>
                    </Box>
                    <Box>
                      <Box className="cb-kpi-label">Worst</Box>
                      <Box className="cb-mono" sx={{ fontSize: 14, fontWeight: 600, mt: 0.3 }}>{fmtScore(s.max_score)}</Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          {/* Hazard scenario comparison */}
          <Paper sx={{ p: 2.4, mb: 2 }} data-testid="hazard-scenario-comparison">
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5 }}>
              <TrendingUp size={16} color="#2E5A35" />
              <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>
                Hazard Severity Drift
              </Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hazard</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">2030</TableCell>
                  <TableCell align="right">2050</TableCell>
                  <TableCell align="right">Δ 2050 - Current</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // unify hazard ids across scenarios
                  const ids = new Set();
                  SCENS.forEach((sc) => (data[sc]?.summary?.hazard_breakdown || []).forEach((h) => ids.add(h.hazard_id)));
                  const arr = Array.from(ids);
                  const get = (sc, id) => (data[sc]?.summary?.hazard_breakdown || []).find((h) => h.hazard_id === id);
                  arr.sort((a, b) => (get("2050", b)?.avg_score || 0) - (get("2050", a)?.avg_score || 0));
                  return arr.map((id) => {
                    const c0 = get("current", id)?.avg_score || 0;
                    const c30 = get("2030", id)?.avg_score || 0;
                    const c50 = get("2050", id)?.avg_score || 0;
                    const delta = c50 - c0;
                    return (
                      <TableRow key={id}>
                        <TableCell sx={{ fontWeight: 600 }}>{HAZARD_LABELS[id] || id}</TableCell>
                        <TableCell align="right" className="cb-mono">{fmtScore(c0)}</TableCell>
                        <TableCell align="right" className="cb-mono">{fmtScore(c30)}</TableCell>
                        <TableCell align="right" className="cb-mono">{fmtScore(c50)}</TableCell>
                        <TableCell align="right" className="cb-mono" sx={{ color: delta > 0 ? "#A93226" : "#2E5A35", fontWeight: 600 }}>
                          {delta > 0 ? "+" : ""}{delta.toFixed(1)}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </Paper>

          {/* Per-asset scenario drift */}
          <Paper sx={{ p: 0, overflow: "hidden" }} data-testid="per-asset-scenario-drift">
            <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA" }}>
              <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                Per-Collateral Scenario Drift
              </Box>
              <Box sx={{ fontSize: 12, color: "#6B7280" }}>Tracks which assets cross risk-level thresholds in 2030 / 2050.</Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Collateral</TableCell>
                  <TableCell>Borrower</TableCell>
                  <TableCell align="center">Current</TableCell>
                  <TableCell align="center">2030</TableCell>
                  <TableCell align="center">2050</TableCell>
                  <TableCell align="right">Δ Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const cur = data.current?.results || [];
                  return cur.map((r) => {
                    const m = {
                      current: r,
                      "2030": (data["2030"]?.results || []).find((x) => x.collateral_id === r.collateral_id),
                      "2050": (data["2050"]?.results || []).find((x) => x.collateral_id === r.collateral_id),
                    };
                    const delta = (m["2050"]?.overall_score || 0) - (m.current?.overall_score || 0);
                    return (
                      <TableRow key={r.collateral_id}>
                        <TableCell sx={{ fontWeight: 600 }}>{r.collateral_name}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.borrower_name}</TableCell>
                        {SCENS.map((sc) => (
                          <TableCell key={sc} align="center">
                            <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.4 }}>
                              <Box className="cb-mono" sx={{ fontSize: 12, fontWeight: 600 }}>{fmtScore(m[sc]?.overall_score)}</Box>
                              {m[sc] && <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: riskColor(m[sc]?.risk_level) }} />}
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600, color: delta > 0 ? "#A93226" : "#2E5A35" }}>
                          {delta > 0 ? "+" : ""}{delta.toFixed(1)}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </DashboardLayout>
  );
}
